// Activity: the outreach log, the owner's own notes, and the legal card (04 §2).
//
// Outreach is append-only. A call that went badly is not edited into a call that went well —
// "why do we lose" is answerable only if the history is intact, and the loss analytics in B6 read
// exactly these rows.
//
// Notes are the opposite: one field, overwritten, and saved on a debounce rather than a button,
// because they are written while talking and nobody presses Save mid-sentence.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isConfigured, isDemoMode, supabase } from '../../lib/supabase.js';
import { DEMO_LEGAL_ENTITY } from '../../lib/mockData.js';

export interface OutreachEvent {
  id: number;
  channel: string;
  outcome: string;
  loss_reason: string | null;
  notes: string | null;
  next_follow_up: string | null;
  happened_at: string;
}

export interface FinancialRow {
  year: number;
  revenue_bgn: number | null;
  profit_bgn: number | null;
  employees: number | null;
}

export interface LegalEntity {
  id?: number;
  legal_name: string | null;
  eik: string | null;
  legal_form: string | null;
  company_status: string | null;
  registration_date?: string | null;
  registered_seat: string | null;
  seat_city: string | null;
  manager_names: string[];
  owner_names: string[];
  match_confidence: number | null;
  match_method: string | null;
  registry_dump_date: string | null;
  source: string | null;
  source_url?: string | null;
  is_from_parent?: boolean;
  financials?: FinancialRow[];
}

export const OUTCOMES = [
  'без отговор',
  'разговор — интерес',
  'разговор — без интерес',
  'изпратена оферта',
  'уговорена среща',
  'друго',
] as const;

export const LOSS_REASONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'too_expensive', label: 'Скъпо' },
  { value: 'has_someone', label: 'Имат друг' },
  { value: 'no_interest', label: 'Няма интерес' },
  { value: 'bad_timing', label: 'Не е моментът' },
  { value: 'other', label: 'Друго' },
];

export function useOutreach(leadId: number | null) {
  return useQuery({
    queryKey: ['outreach', leadId],
    enabled: leadId !== null,
    queryFn: async (): Promise<OutreachEvent[]> => {
      if (!isConfigured || isDemoMode) return [];
      const { data, error } = await supabase
        .from('outreach_events')
        .select('id, channel, outcome, loss_reason, notes, next_follow_up, happened_at')
        .eq('lead_id', leadId!)
        .order('happened_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as OutreachEvent[];
    },
  });
}

export function useLogOutreach(leadId: number | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (event: {
      channel: string;
      outcome: string;
      lossReason: string | null;
      notes: string;
      nextFollowUp: string | null;
    }) => {
      if (!isConfigured || isDemoMode) return;
      const { error } = await supabase.from('outreach_events').insert({
        lead_id: leadId,
        channel: event.channel,
        outcome: event.outcome,
        loss_reason: event.lossReason,
        notes: event.notes === '' ? null : event.notes,
        next_follow_up: event.nextFollowUp,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['outreach', leadId] });
      void client.invalidateQueries({ queryKey: ['follow_ups'] });
      void client.invalidateQueries({ queryKey: ['top_unworked'] });
    },
  });
}

/**
 * The lead id travels WITH the text, not with the hook. A debounced save that fired after the
 * owner had clicked another lead used the hook's current id — TanStack re-syncs mutation
 * options on every render — and wrote lead A's notes over lead B's.
 */
export function useSaveNotes() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, notes }: { leadId: number; notes: string }) => {
      if (!isConfigured || isDemoMode) return;
      const { error } = await supabase.from('leads').update({ my_notes: notes }).eq('id', leadId);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_result, { leadId }) =>
      void client.invalidateQueries({ queryKey: ['lead', leadId] }),
  });
}

const LEGAL_SELECT =
  'id, legal_name, eik, legal_form, company_status, registration_date, registered_seat, seat_city,' +
  ' manager_names, owner_names, match_confidence, match_method, registry_dump_date, source, source_url';

export function useLegalEntity(leadId: number | null, parentLeadId?: number | null) {
  return useQuery({
    queryKey: ['legal_entity', leadId, parentLeadId ?? null],
    enabled: leadId !== null,
    queryFn: async (): Promise<LegalEntity | null> => {
      if (!isConfigured || isDemoMode) return DEMO_LEGAL_ENTITY as unknown as LegalEntity;

      const { data, error } = await supabase
        .from('legal_entities')
        .select(LEGAL_SELECT)
        .eq('lead_id', leadId!)
        .maybeSingle();
      if (error) throw new Error(error.message);

      let entity = (data as unknown as LegalEntity | null) ?? null;
      if (!entity && parentLeadId) {
        const { data: parentData, error: parentErr } = await supabase
          .from('legal_entities')
          .select(LEGAL_SELECT)
          .eq('lead_id', parentLeadId)
          .maybeSingle();
        if (parentErr) throw new Error(parentErr.message);
        if (parentData) {
          entity = { ...(parentData as unknown as LegalEntity), is_from_parent: true };
        }
      }

      if (entity?.id) {
        const { data: finData, error: finErr } = await supabase
          .from('financials')
          .select('year, revenue_bgn, profit_bgn, employees')
          .eq('legal_entity_id', entity.id)
          .order('year', { ascending: false });
        if (!finErr && finData) {
          entity = { ...entity, financials: finData as unknown as FinancialRow[] };
        }
      }

      return entity;
    },
  });
}

export function useApplyManualEntity() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, eik }: { leadId: number; eik: string }) => {
      if (!isConfigured || isDemoMode) return;
      const { error } = await supabase.rpc('apply_manual_entity', {
        p_lead_id: leadId,
        p_eik: eik.trim(),
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: (_res, { leadId }) => {
      void client.invalidateQueries({ queryKey: ['legal_entity', leadId] });
      void client.invalidateQueries({ queryKey: ['legal_entity'] });
      void client.invalidateQueries({ queryKey: ['review_queue'] });
      void client.invalidateQueries({ queryKey: ['manual_review'] });
      void client.invalidateQueries({ queryKey: ['manual_review_open'] });
      void client.invalidateQueries({ queryKey: ['lead', leadId] });
      void client.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useSetStage(leadId: number | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (stageId: number | null) => {
      if (!isConfigured || isDemoMode) return;
      const { error } = await supabase
        .from('leads')
        .update({ sales_stage_id: stageId })
        .eq('id', leadId!);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['lead', leadId] });
      void client.invalidateQueries({ queryKey: ['leads'] });
      void client.invalidateQueries({ queryKey: ['board_leads'] });
    },
  });
}

export function useToggleDnc(leadId: number | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (doNotContact: boolean) => {
      if (!isConfigured || isDemoMode) return;
      const { error } = await supabase
        .from('leads')
        .update({ do_not_contact: doNotContact })
        .eq('id', leadId!);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['lead', leadId] });
      void client.invalidateQueries({ queryKey: ['leads'] });
      void client.invalidateQueries({ queryKey: ['board_leads'] });
      void client.invalidateQueries({ queryKey: ['top_unworked'] });
    },
  });
}
